import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, Clock, Tag, ArrowLeft, Check } from "lucide-react";
import { blogPosts as mockBlogList, companyInfo as mockCompany, galleryImages } from "../data/mock";
import { useBlogPost, useBlogPosts } from "../hooks/useContent";
import PageHero from "../components/PageHero";

const ContentRenderer = ({ content }) => {
  return content.map((block, i) => {
    switch (block.type) {
      case "heading":
        return (
          <h2 key={i} className="text-xl sm:text-2xl font-semibold mt-12 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            {block.text}
          </h2>
        );
      case "subheading":
        return (
          <h3 key={i} className="text-lg font-semibold mt-8 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {block.text}
          </h3>
        );
      case "paragraph":
        return (
          <p key={i} className="text-[15px] leading-relaxed mb-4" style={{ color: "#444" }}>
            {block.text}
          </p>
        );
      case "list":
        return (
          <ul key={i} className="space-y-2 mb-6 ml-1">
            {block.items.map((item, j) => (
              <li key={j} className="flex items-start gap-3 text-[15px]" style={{ color: "#444" }}>
                <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "var(--gold)" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case "highlight":
        return (
          <div key={i} className="p-6 rounded-2xl my-8" style={{ backgroundColor: "var(--gold-bg)", borderLeft: "4px solid var(--gold)" }}>
            <p className="text-[15px] leading-relaxed font-medium" style={{ color: "#333" }}>
              {block.text}
            </p>
          </div>
        );
      case "cta":
        return (
          <div key={i} className="text-center my-10 p-8 rounded-2xl" style={{ backgroundColor: "var(--black)" }}>
            <p className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {block.text}
            </p>
            <a
              href={`tel:${mockCompany.phone}`}
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold no-underline transition-all hover:shadow-lg"
              style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
              data-testid="blog-cta-phone"
            >
              <Phone className="w-4 h-4" />
              {mockCompany.phone}
            </a>
          </div>
        );
      default:
        return null;
    }
  });
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { data: post } = useBlogPost(slug);
  const { data: allPosts } = useBlogPosts();

  const pageTitle = post
    ? (post.metaTitle || post.title + " | Casa Funerară Sfântul Andrei")
    : "Articol – Casa Funerară Sfântul Andrei";
  const pageDescription = post ? (post.metaDescription || post.excerpt) : "";
  const canonicalUrl = "https://funeraresfantulandrei.ro/blog/" + slug;
  const ogImageUrl = post?.image ? ("https://funeraresfantulandrei.ro" + post.image) : null;

  useEffect(() => { document.title = pageTitle; }, [pageTitle]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  if (!post.content) {
    return null;
  }

  const otherPosts = allPosts.filter((p) => p.slug !== slug && p.content).slice(0, 2);

  return (
    <div>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      <PageHero
        image={post.image || galleryImages.sfAndreiIcoana}
        title={post.title}
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Blog", to: "/blog" },
          { label: post.category },
        ]}
      />

      <section className="section-spacing bg-white">
        <div className="max-w-[800px] mx-auto px-4 lg:px-8">
          {/* Article meta */}
          <div className="flex items-center gap-4 mb-10 pb-8" style={{ borderBottom: "1px solid var(--warm-border)" }}>
            <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ color: "var(--gold)", backgroundColor: "var(--gold-bg)" }}>
              <Tag className="w-3 h-3" />
              {post.category}
            </span>
            <span className="flex items-center gap-1.5 text-xs" style={{ color: "#999" }}>
              <Clock className="w-3 h-3" />
              {post.readTime} citire
            </span>
            <span className="text-xs" style={{ color: "#ccc" }}>{post.date}</span>
          </div>

          {/* Article content */}
          <article data-testid="blog-article-content">
            <ContentRenderer content={post.content} />
          </article>

          {/* Back to blog */}
          <div className="mt-16 pt-8" style={{ borderTop: "1px solid var(--warm-border)" }}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold no-underline transition-all"
              style={{ color: "var(--gold)" }}
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi la Blog
            </Link>
          </div>

          {/* Related posts */}
          {otherPosts.length > 0 && (
            <div className="mt-16">
              <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Articole Similare
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {otherPosts.map((p, i) => (
                  <Link key={i} to={`/blog/${p.slug}`} className="blog-card group no-underline block">
                    {p.image && (
                      <div className="img-hover-zoom aspect-[16/9]">
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5">
                      <h4 className="text-base font-semibold mb-2 group-hover:opacity-70 transition-opacity" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {p.title}
                      </h4>
                      <span className="text-xs" style={{ color: "#999" }}>{p.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-5 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Aveți Nevoie de Sprijin?
          </h2>
          <p className="text-white/50 max-w-xl mx-auto mb-10">
            Suntem disponibili NON-STOP, 24/7. Contactați-ne pentru orice întrebare.
          </p>
          <a
            href={`tel:${mockCompany.phone}`}
            className="inline-flex items-center gap-3 px-10 py-4 text-sm font-semibold tracking-wide no-underline transition-all hover:shadow-lg"
            style={{ borderRadius: "var(--radius-pill)", backgroundColor: "var(--gold)", color: "var(--black)" }}
          >
            <Phone className="w-4 h-4" />
            {mockCompany.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
